/**
 * SPINE API service
 * Handles all SPINE service integration: standards preview, node navigation, and competency search
 */

import apiClient from './api';
import { Standard, StandardsPreviewRequest, SpineNodeSubject, SpineNodeTree, CompetencySearchResponse, LimiterGroup, LIMITER_TYPE_MATHTASTIC, SemanticSearchRequest, SemanticSearchResponse, FiltersResponse, FlattenedStandard } from '../types';

// ============================================================================
// Types for SPINE Standards Filtering
// ============================================================================

export interface StandardsFilterParams {
  grades: string[];
  documents: string[];
  jurisdictions: string[];
  states: string[];
}

// ============================================================================
// SPINE Standards Normalization Functions
// ============================================================================

/**
 * Normalizes a raw SPINE standard from the API into the unified Standard format.
 * Handles various field name formats (snake_case, camelCase) defensively.
 */
export function normalizeStandard(raw: any): Standard {
  // Handle grade field - could be string, array (take first), or various field names
  let grade = '';
  if (raw.grade) {
    grade = Array.isArray(raw.grade) ? raw.grade[0] : raw.grade;
  } else if (raw.gradeLabels) {
    grade = Array.isArray(raw.gradeLabels) ? raw.gradeLabels[0] : raw.gradeLabels;
  } else if (raw.grade_labels) {
    grade = Array.isArray(raw.grade_labels) ? raw.grade_labels[0] : raw.grade_labels;
  } else if (raw.grades) {
    grade = Array.isArray(raw.grades) ? raw.grades[0] : raw.grades;
  }

  return {
    standardId: raw.standard_id || raw.standardId || '',
    code: raw.code || raw.itemCode || raw.item_code || '',
    description: raw.description || raw.itemDescription || raw.item_description || '',
    document: raw.document || raw.documentCode || raw.document_code || '',
    grade,
    jurisdiction: raw.jurisdiction || raw.standardJurisdiction || raw.standard_jurisdiction || '',
    state: raw.state || raw.usStatePostalCode || raw.us_state_postal_code,
    subject: raw.subject,
  };
}

/**
 * Normalizes an array of raw SPINE standards from the API.
 */
export function normalizeStandards(rawStandards: any[]): Standard[] {
  if (!rawStandards || !Array.isArray(rawStandards)) {
    return [];
  }
  return rawStandards.map(normalizeStandard);
}

// ============================================================================
// SPINE Standards Filtering Functions
// ============================================================================

/**
 * Filters a list of SPINE standards based on the provided filter parameters.
 *
 * Filter behavior:
 * - Empty filter arrays mean "no filter" (all values pass)
 * - Grade filter: standard's grade must be in the list
 * - Document filter: standard's document must be in the list
 * - Jurisdiction filter: standard's jurisdiction must be in the list
 * - State filter: only applies to STATE jurisdiction standards; they must have a matching state
 */
export function filterStandards(
  standards: Standard[],
  filters: StandardsFilterParams
): Standard[] {
  if (!standards || standards.length === 0) {
    return [];
  }

  const { grades, documents, jurisdictions, states } = filters;

  return standards.filter((std) => {
    // Grade filter
    if (grades.length > 0 && !grades.includes(std.grade)) {
      return false;
    }

    // Document filter
    if (documents.length > 0 && !documents.includes(std.document)) {
      return false;
    }

    // Jurisdiction filter
    if (jurisdictions.length > 0 && !jurisdictions.includes(std.jurisdiction)) {
      return false;
    }

    // State filter - filter by state property regardless of jurisdiction
    if (states.length > 0 && (!std.state || !states.includes(std.state))) {
      return false;
    }

    return true;
  });
}

// ============================================================================
// Limiter Group Helper Functions
// ============================================================================

/**
 * Checks if a limiter group contains mathtastic (P-type) limiters.
 *
 * Mathtastic limiters are regex-based patterns for matching mathematical
 * expressions in content. They are identified by having type="P".
 *
 * @param limiterGroup - The limiter group to check
 * @returns true if the group contains one or more P-type limiters
 */
export function isLimiterGroupMathtastic(limiterGroup: LimiterGroup): boolean {
  return limiterGroup.limiters.some(limiter => limiter.type === LIMITER_TYPE_MATHTASTIC);
}

// ============================================================================
// SPINE API Functions
// ============================================================================

export const spineApi = {
  // Semantic Search API - for searching standards by natural language description
  semanticSearch: {
    /**
     * Get available filters for semantic search.
     * Returns filter metadata including valid values for each filter.
     */
    async getFilters(): Promise<FiltersResponse> {
      const response = await apiClient.get<FiltersResponse>('/standards/filters');
      return response.data;
    },

    /**
     * Search for standards using semantic similarity.
     */
    async search(request: SemanticSearchRequest): Promise<SemanticSearchResponse> {
      const response = await apiClient.post<SemanticSearchResponse>('/standards/semantic-search', request);
      return response.data;
    },
  },

  // Standards API - for getting aligned standards based on competencies
  standards: {
    /**
     * Get SPINE standards preview for a single competency/limiter combination.
     * Returns normalized Standard objects.
     */
    async preview(request: StandardsPreviewRequest): Promise<Standard[]> {
      const response = await apiClient.post<any[]>('/standards/preview', request);
      return normalizeStandards(response.data);
    },

    /**
     * Get SPINE standards preview for multiple competencies in parallel (batch).
     * Returns normalized Standard objects keyed by competency:limiters.
     *
     * Uses chunked fetching to avoid timeouts on large request sets:
     * - Splits requests into chunks of CHUNK_SIZE
     * - Processes MAX_CONCURRENT_CHUNKS in parallel
     * - Aggregates all results before returning
     */
    async batchPreview(
      requests: StandardsPreviewRequest[]
    ): Promise<Record<string, Standard[]>> {
      const CHUNK_SIZE = 10;
      const MAX_CONCURRENT_CHUNKS = 3;

      // If small enough, just send directly (no chunking overhead)
      if (requests.length <= CHUNK_SIZE) {
        const response = await apiClient.post<Record<string, any[]>>(
          '/standards/batch-preview',
          requests
        );
        const normalized: Record<string, Standard[]> = {};
        for (const [key, standards] of Object.entries(response.data)) {
          normalized[key] = normalizeStandards(standards);
        }
        return normalized;
      }

      // Split requests into chunks
      const chunks: StandardsPreviewRequest[][] = [];
      for (let i = 0; i < requests.length; i += CHUNK_SIZE) {
        chunks.push(requests.slice(i, i + CHUNK_SIZE));
      }

      // Process chunks in waves of MAX_CONCURRENT_CHUNKS
      const allResults: Record<string, Standard[]> = {};

      for (let i = 0; i < chunks.length; i += MAX_CONCURRENT_CHUNKS) {
        const wave = chunks.slice(i, i + MAX_CONCURRENT_CHUNKS);

        const wavePromises = wave.map(async (chunk) => {
          const response = await apiClient.post<Record<string, any[]>>(
            '/standards/batch-preview',
            chunk
          );
          return response.data;
        });

        const waveResults = await Promise.allSettled(wavePromises);

        // Aggregate results from this wave
        waveResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            for (const [key, standards] of Object.entries(result.value)) {
              allResults[key] = normalizeStandards(standards);
            }
          } else {
            console.error('Chunk fetch failed:', result.reason);
          }
        });
      }

      return allResults;
    },

    /**
     * Get a single flattened standard by ID.
     */
    async getFlattened(standardId: string): Promise<FlattenedStandard> {
      const response = await apiClient.get<FlattenedStandard>(`/standards/flattened/${standardId}`);
      return response.data;
    },

    /**
     * Get multiple flattened standards by IDs (batch).
     * Returns a map of standardId -> FlattenedStandard.
     */
    async getFlattenedBatch(standardIds: string[]): Promise<Record<string, FlattenedStandard>> {
      const response = await apiClient.post<Record<string, FlattenedStandard>>(
        '/standards/flattened/batch',
        { standardIds }
      );
      return response.data;
    },
  },

  // Nodes API - for navigating SPINE node hierarchy to find competencies
  nodes: {
    /**
     * Get list of SPINE subject nodes (top-level nodes in the hierarchy).
     */
    async getSubjects(): Promise<SpineNodeSubject[]> {
      const response = await apiClient.get<SpineNodeSubject[]>('/spine-nodes/subjects');
      return response.data;
    },

    /**
     * Get SPINE node tree and its children by node ID.
     * Used to navigate down the hierarchy to find specific competencies.
     */
    async getTree(nodeId: string): Promise<SpineNodeTree> {
      const response = await apiClient.get<SpineNodeTree>(`/spine-nodes/tree/${nodeId}`);
      return response.data;
    },

    /**
     * Get direct children of a SPINE node (competencies).
     * Used to fetch competencies when a competency parent is selected.
     */
    async getChildren(nodeId: string): Promise<SpineNodeTree[]> {
      const response = await apiClient.get<SpineNodeTree[]>(`/spine-nodes/${nodeId}/children`);
      return response.data;
    },

    /**
     * Get tag candidates for multiple competency IDs.
     * Used to generate tag candidates from selected competencies.
     */
    async getTagCandidatesByCompetencyIds(competencyIds: string[]): Promise<any> {
      // Build query string with multiple competencyIds parameters
      const queryParams = competencyIds.map(id => `competencyIds=${encodeURIComponent(id)}`).join('&');
      const url = `/spine-nodes/tag-candidates/by-competency-ids?${queryParams}`;

      const response = await apiClient.get<any>(url);
      return response.data;
    },

    /**
     * Create tag candidates from competency IDs.
     * Returns tag candidates for adding to in-memory predictions (not saved to S3 yet).
     */
    async createTagCandidates(competencyIds: string[]): Promise<{
      success: boolean;
      tagCandidates: any[];
      count: number;
    }> {
      const response = await apiClient.post<{
        success: boolean;
        tagCandidates: any[];
        count: number;
      }>('/spine-nodes/create-tag-candidates', {
        competencyIds
      });

      return response.data;
    },

    /**
     * Search for competencies using subject and keyword filters
     */
    async searchCompetencies(subjectFilter: string, keywords: string[]): Promise<CompetencySearchResponse> {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('uriSegmentFilter', subjectFilter);

      // Add each keyword as a separate titleSegmentFilter parameter
      keywords.forEach(keyword => {
        if (keyword.trim()) {
          params.append('titleSegmentFilter', keyword.trim());
        }
      });

      const response = await apiClient.get<CompetencySearchResponse>(
        `/spine-nodes/competency-search?${params.toString()}`
      );

      return response.data;
    },
  },
};

// Legacy exports for backwards compatibility
export const standardsApi = spineApi.standards;
export const spineNodesApi = spineApi.nodes;
